import ReactDOM from "react-dom";
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, X, ChevronDown, AlertCircle, Settings, ArrowUpDown, ArrowUp, ArrowDown, Download } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

const STATUS_LABELS = {
  pending: "PENDING", ci_done: "CI DONE", dtm_done: "DTM DONE",
  dtc_done: "DTC DONE", mi_done: "MI DONE", qc1_pending: "QC1",
  qc2_pending: "QC2", qc3_pending: "QC3", qc_passed: "QC PASSED",
  rework: "REWORK", wcc_gen: "WCC", invoiced: "INVOICED", paid: "PAID"
};

const STATUS_COLORS = {
  pending: "bg-slate-100 text-slate-600 border-slate-200",
  ci_done: "bg-blue-100 text-blue-700 border-blue-200",
  dtm_done: "bg-purple-100 text-purple-700 border-purple-200",
  dtc_done: "bg-yellow-100 text-yellow-700 border-yellow-200",
  mi_done: "bg-orange-100 text-orange-700 border-orange-200",
  qc_passed: "bg-green-100 text-green-700 border-green-200",
  rework: "bg-red-100 text-red-700 border-red-200",
};

const HOLD_COLORS = {
  on_hold: "bg-red-100 text-red-700 border-red-200",
  excluded: "bg-slate-200 text-slate-700 border-slate-300",
  none: ""
};

const getNextAction = (status, holdStatus) => {
  if (holdStatus !== "none") return null;
  const map = {
    pending: { label: "Survey", path: "survey", color: "text-blue-600" },
    ci_done: { label: "DTM", path: "dtm", color: "text-purple-600" },
    dtm_done: { label: "DTC", path: "dtc", color: "text-yellow-600" },
    dtc_done: { label: "MI", path: "mi", color: "text-orange-600" },
  };
  return map[status] || null;
};

const fmtDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return ""; }
};

// All available columns with groups
const ALL_COLUMNS = [
  // Core
  { key: "dt_code", label: "DT Code", group: "Core", default: true, sticky: true },
  { key: "dt_name", label: "DT Name", group: "Core", default: true },
  { key: "feeder_name", label: "Feeder", group: "Core", default: true },
  { key: "feeder_code", label: "Feeder Code", group: "Core", default: false },
  { key: "transformer_capacity_kva", label: "Capacity (kVA)", group: "Core", default: true },
  { key: "lifecycle_status", label: "Status", group: "Core", default: true },
  { key: "hold_status", label: "Hold", group: "Core", default: true },
  { key: "location_name", label: "Location", group: "Core", default: false },
  { key: "address", label: "Address", group: "Core", default: false },
  // Hierarchy
  { key: "division_name", label: "Division", group: "Hierarchy", default: false },
  { key: "sub_division_name", label: "Sub Division", group: "Hierarchy", default: false },
  { key: "sub_station_name", label: "Sub Station", group: "Hierarchy", default: false },
  { key: "package_name", label: "Package", group: "Hierarchy", default: false },
  // CI/Survey
  { key: "survey_date", label: "CI Date", group: "CI (Survey)", default: true, isDate: true },
  { key: "surveyor_name", label: "CI Surveyor", group: "CI (Survey)", default: false },
  { key: "survey_capacity_kva", label: "CI Capacity", group: "CI (Survey)", default: false },
  { key: "survey_pole_type", label: "Pole Type", group: "CI (Survey)", default: false },
  { key: "survey_cable_type", label: "CI Cable Type", group: "CI (Survey)", default: false },
  { key: "survey_cable_size", label: "CI Cable mm\u00B2", group: "CI (Survey)", default: false },
  { key: "survey_cable_length", label: "CI Cable mtr", group: "CI (Survey)", default: false },
  // DTM
  { key: "dtm_date", label: "DTM Date", group: "DTM", default: true, isDate: true },
  { key: "dtm_technician", label: "DTM Technician", group: "DTM", default: true },
  { key: "dtm_meter_serial", label: "Meter Serial", group: "DTM", default: true },
  { key: "dtm_capacity_kva", label: "DTM Capacity", group: "DTM", default: false },
  { key: "dtm_old_meter_serial", label: "Old Meter No", group: "DTM", default: false },
  { key: "dtm_old_meter_reading", label: "Old kWh", group: "DTM", default: false },
  { key: "dtm_old_meter_make", label: "Old Meter Make", group: "DTM", default: false },
  { key: "dtm_cable_installed", label: "Cable Installed", group: "DTM", default: false },
  { key: "dtm_cable_type", label: "DTM Cable Type", group: "DTM", default: false },
  { key: "dtm_cable_size", label: "DTM Cable mm\u00B2", group: "DTM", default: false },
  { key: "dtm_cable_length", label: "DTM Cable mtr", group: "DTM", default: false },
  // DTC
  { key: "dtc_date", label: "DTC Date", group: "DTC", default: true, isDate: true },
  { key: "dtc_technician", label: "DTC Technician", group: "DTC", default: true },
  // MI
  { key: "mi_date", label: "MI Date", group: "MI", default: true, isDate: true },
  { key: "mi_technician", label: "MI Technician", group: "MI", default: true },
  { key: "mi_meter_verified", label: "MI Meter Verified", group: "MI", default: false },
  { key: "mi_mrn_book_no", label: "MRN Book", group: "MI", default: false },
  { key: "mi_seal_serial", label: "Seal No", group: "MI", default: false },
  { key: "mi_meter_reading", label: "MI kWh", group: "MI", default: false },
  // Action is always last, handled separately
];

const STORAGE_KEY = "dt_master_visible_cols";
const IN_PROGRESS_STATUSES = ["ci_done", "dtm_done", "dtc_done", "qc1_pending", "qc2_pending", "qc3_pending", "rework"];

function getDefaultVisible() {
  return ALL_COLUMNS.filter(c => c.default).map(c => c.key);
}

function loadVisibleCols() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return getDefaultVisible();
}

export default function DTMasterPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const [dts, setDTs] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const [filterOptions, setFilterOptions] = useState({ circles: [], feeders: [], show_circle_filter: false });
  const [circleFeeders, setCircleFeeders] = useState([]);

  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [selectedCircle, setSelectedCircle] = useState(searchParams.get("circle_name") || "");
  const [selectedFeeder, setSelectedFeeder] = useState(searchParams.get("feeder_code") || "");
  const [selectedStatuses, setSelectedStatuses] = useState(
    searchParams.get("status")?.split(",").filter(Boolean) || []
  );
  const [selectedHold, setSelectedHold] = useState(searchParams.get("hold_status") || "");

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(0);
  const [feeders, setFeeders] = useState([]);
  const [showCircleDropdown, setShowCircleDropdown] = useState(false);
  const [showFeederDropdown, setShowFeederDropdown] = useState(false);
  const LIMIT = 30;

  // Column state
  const [visibleCols, setVisibleCols] = useState(loadVisibleCols);
  const [showColSelector, setShowColSelector] = useState(false);
  const [sortField, setSortField] = useState("dt_code");
  const [sortDir, setSortDir] = useState("asc");
  const [columnFilters, setColumnFilters] = useState({});
  const colSelectorRef = useRef(null);

  const [newDT, setNewDT] = useState({ dt_code: "", dt_name: "", feeder_code: "", circle_name: "", address: "" });

  // Save column preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols));
  }, [visibleCols]);

  // Close column selector on outside click
  useEffect(() => {
    const handler = (e) => {
      if (colSelectorRef.current && !colSelectorRef.current.contains(e.target)) setShowColSelector(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const activeColumns = useMemo(() => ALL_COLUMNS.filter(c => visibleCols.includes(c.key)), [visibleCols]);

  // Fetch filter options
  useEffect(() => {
    api.get("/dt-master/filter-options").then(r => {
      setFilterOptions(r.data);
      if (r.data.feeders?.length > 0) setCircleFeeders(r.data.feeders);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedCircle && filterOptions.show_circle_filter) {
      api.get(`/dt-master/feeders-for-circle/${selectedCircle}`).then(r => setCircleFeeders(r.data)).catch(() => {});
    } else if (!selectedCircle && filterOptions.circles?.length > 0) {
      setCircleFeeders([]);
    }
  }, [selectedCircle, filterOptions.show_circle_filter, filterOptions.circles]);

  // Fetch enriched DTs
  const fetchDTs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * LIMIT, limit: LIMIT, sort_field: sortField, sort_dir: sortDir };
      if (search) params.search = search;
      if (selectedCircle) params.circle_name = selectedCircle;
      if (selectedFeeder) params.feeder_code = selectedFeeder;
      if (selectedStatuses.length === 1) params.lifecycle_status = selectedStatuses[0];
      if (selectedHold) params.hold_status = selectedHold;

      const res = await api.get("/dt-master/enriched", { params });
      setDTs(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error("Failed to load DTs");
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedCircle, selectedFeeder, selectedStatuses, selectedHold, sortField, sortDir]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (selectedCircle) params.circle_name = selectedCircle;
      const res = await api.get("/dt-master/stats", { params });
      setStats(res.data);
    } catch {}
  }, [selectedCircle]);

  useEffect(() => { fetchDTs(); }, [fetchDTs]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { api.get("/hierarchy/feeders").then(r => setFeeders(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (selectedCircle) params.set("circle_name", selectedCircle);
    if (selectedFeeder) params.set("feeder_code", selectedFeeder);
    if (selectedStatuses.length > 0) params.set("status", selectedStatuses.join(","));
    if (selectedHold) params.set("hold_status", selectedHold);
    setSearchParams(params, { replace: true });
  }, [search, selectedCircle, selectedFeeder, selectedStatuses, selectedHold, setSearchParams]);

  const handleSearch = (e) => { e.preventDefault(); setPage(0); };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
    setPage(0);
  };

  const toggleStatus = (status) => {
    setSelectedStatuses(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);
    setPage(0);
  };

  const clearAllFilters = () => {
    setSearch(""); setSelectedCircle(""); setSelectedFeeder(""); setSelectedStatuses([]); setSelectedHold(""); setColumnFilters({}); setPage(0);
  };

  const toggleCol = (key) => {
    setVisibleCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const toggleGroup = (group) => {
    const groupKeys = ALL_COLUMNS.filter(c => c.group === group).map(c => c.key);
    const allOn = groupKeys.every(k => visibleCols.includes(k));
    if (allOn) setVisibleCols(prev => prev.filter(k => !groupKeys.includes(k)));
    else setVisibleCols(prev => [...new Set([...prev, ...groupKeys])]);
  };

  // Client-side column filter
  const filteredDTs = useMemo(() => {
    const filters = Object.entries(columnFilters).filter(([, v]) => v);
    if (filters.length === 0) return dts;
    return dts.filter(dt => filters.every(([key, val]) => {
      const cellVal = (dt[key] ?? "").toString().toLowerCase();
      return cellVal.includes(val.toLowerCase());
    }));
  }, [dts, columnFilters]);

  // Export to Excel
  const handleExport = async () => {
    toast.info("Preparing export...");
    try {
      const params = { skip: 0, limit: 5000, sort_field: sortField, sort_dir: sortDir };
      if (search) params.search = search;
      if (selectedCircle) params.circle_name = selectedCircle;
      if (selectedFeeder) params.feeder_code = selectedFeeder;
      if (selectedStatuses.length === 1) params.lifecycle_status = selectedStatuses[0];
      if (selectedHold) params.hold_status = selectedHold;

      const res = await api.get("/dt-master/enriched", { params });
      const rows = res.data.items.map(dt => {
        const row = {};
        activeColumns.forEach(col => {
          let val = dt[col.key];
          if (col.isDate && val) val = fmtDate(val);
          if (col.key === "lifecycle_status") val = STATUS_LABELS[val] || val;
          if (col.key === "dtm_cable_installed") val = val === true ? "Yes" : val === false ? "No" : "";
          row[col.label] = val ?? "";
        });
        return row;
      });
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "DT Master");
      XLSX.writeFile(wb, `DT_Master_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${rows.length} rows`);
    } catch {
      toast.error("Export failed");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newDT.dt_code?.trim()) { toast.error("DT Code is required"); return; }
    if (!newDT.feeder_code) { toast.error("Please select a feeder"); return; }
    setCreating(true);
    try {
      const feeder = feeders.find(f => f.feeder_code === newDT.feeder_code);
      await api.post("/dt-master", { ...newDT, circle_name: feeder?.circle_name || newDT.circle_name });
      toast.success("DT created successfully");
      setShowCreate(false);
      setNewDT({ dt_code: "", dt_name: "", feeder_code: "", circle_name: "", address: "" });
      fetchDTs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create DT");
    } finally { setCreating(false); }
  };

  const hasActiveFilters = search || selectedCircle || selectedFeeder || selectedStatuses.length > 0 || selectedHold;
  const inProgressCount = IN_PROGRESS_STATUSES.reduce((sum, s) => sum + (stats.by_status?.[s] || 0), 0);
  const holdCount = (stats.on_hold || 0) + (stats.excluded || 0);

  const renderCell = (dt, col) => {
    const val = dt[col.key];
    if (col.key === "lifecycle_status") {
      return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border whitespace-nowrap ${STATUS_COLORS[val] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
          {STATUS_LABELS[val] || val}
        </span>
      );
    }
    if (col.key === "hold_status") {
      if (val && val !== "none") return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${HOLD_COLORS[val]}`}>
          <AlertCircle size={9} />{val === "on_hold" ? "HOLD" : "EXCL"}
        </span>
      );
      return <span className="text-[10px] text-slate-300">—</span>;
    }
    if (col.key === "dt_code") return <span className="font-mono text-xs text-slate-800 font-semibold whitespace-nowrap">{val}</span>;
    if (col.isDate) return <span className="text-[10px] text-slate-500 whitespace-nowrap">{fmtDate(val)}</span>;
    if (col.key === "dtm_cable_installed") return <span className="text-[10px]">{val === true ? "Yes" : val === false ? "No" : ""}</span>;
    if (col.key === "transformer_capacity_kva" || col.key === "survey_capacity_kva" || col.key === "dtm_capacity_kva")
      return <span className="text-xs text-slate-600">{val ? `${val}` : ""}</span>;
    if (col.key === "dtm_meter_serial" || col.key === "mi_meter_verified")
      return <span className="font-mono text-[10px] text-slate-600">{val || ""}</span>;
    return <span className="text-xs text-slate-600 truncate max-w-[120px] block">{val ?? ""}</span>;
  };

  const colGroups = useMemo(() => {
    const groups = {};
    ALL_COLUMNS.forEach(c => { (groups[c.group] = groups[c.group] || []).push(c); });
    return groups;
  }, []);

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="dt-master-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>DT Master</h1>
          <p className="text-slate-500 text-sm">{total.toLocaleString()} Distribution Transformers</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-sm text-xs font-bold hover:bg-green-700" data-testid="export-excel-btn">
            <Download size={14} /> Export
          </button>
          {/* Column Selector */}
          <div className="relative" ref={colSelectorRef}>
            <button onClick={() => setShowColSelector(!showColSelector)} className="flex items-center gap-1.5 px-3 py-2 border border-slate-200 rounded-sm text-xs font-medium hover:bg-slate-50" data-testid="column-selector-btn">
              <Settings size={14} /> Columns
            </button>
            {showColSelector && (
              <div className="absolute right-0 z-50 mt-1 w-72 max-h-[70vh] overflow-y-auto bg-white border border-slate-200 rounded shadow-xl">
                <div className="sticky top-0 bg-white px-3 py-2 border-b border-slate-100 flex justify-between items-center">
                  <span className="text-xs font-bold text-slate-700">Toggle Columns</span>
                  <button onClick={() => setVisibleCols(getDefaultVisible())} className="text-[10px] text-blue-500 hover:underline">Reset</button>
                </div>
                {Object.entries(colGroups).map(([group, cols]) => (
                  <div key={group} className="border-b border-slate-50 last:border-0">
                    <button onClick={() => toggleGroup(group)} className="w-full px-3 py-1.5 text-left text-[10px] font-bold text-slate-500 uppercase bg-slate-50 hover:bg-slate-100">
                      {group} ({cols.filter(c => visibleCols.includes(c.key)).length}/{cols.length})
                    </button>
                    {cols.map(col => (
                      <label key={col.key} className="flex items-center gap-2 px-4 py-1.5 text-xs hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={visibleCols.includes(col.key)} onChange={() => toggleCol(col.key)}
                          className="rounded border-slate-300 text-orange-500 focus:ring-orange-500/20" />
                        {col.label}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
          {["admin"].includes(user?.role) && (
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600" data-testid="create-dt-btn">
              <Plus size={16} /> Add DT
            </button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-3 space-y-3">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <input type="text" placeholder="Search DT code, name, or feeder..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              value={search} onChange={e => setSearch(e.target.value)} data-testid="dt-search-input" />
          </div>
          <button type="submit" className="px-4 py-2 bg-orange-500 text-white rounded-sm text-xs font-bold hover:bg-orange-600">Search</button>
        </form>
        <div className="flex flex-wrap items-center gap-2">
          {filterOptions.show_circle_filter && (
            <div className="relative">
              <button onClick={() => setShowCircleDropdown(!showCircleDropdown)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedCircle ? "bg-blue-100 text-blue-700 border-blue-300" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                data-testid="circle-filter-btn">{selectedCircle || "All Circles"}<ChevronDown size={12} /></button>
              {showCircleDropdown && (
                <div className="absolute z-50 mt-1 w-48 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
                  <button onClick={() => { setSelectedCircle(""); setSelectedFeeder(""); setShowCircleDropdown(false); setPage(0); }}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${!selectedCircle ? "bg-slate-100 font-medium" : ""}`}>All Circles</button>
                  {filterOptions.circles.map(c => (
                    <button key={c} onClick={() => { setSelectedCircle(c); setSelectedFeeder(""); setShowCircleDropdown(false); setPage(0); }}
                      className={`w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${selectedCircle === c ? "bg-blue-50 text-blue-700 font-medium" : ""}`}>{c}</button>
                  ))}
                </div>
              )}
            </div>
          )}
          {(circleFeeders.length > 0 || filterOptions.feeders?.length > 0) && (
            <div className="relative">
              <button onClick={() => setShowFeederDropdown(!showFeederDropdown)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedFeeder ? "bg-purple-100 text-purple-700 border-purple-300" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
                data-testid="feeder-filter-btn">{selectedFeeder || "All Feeders"}<ChevronDown size={12} /></button>
              {showFeederDropdown && (
                <div className="absolute z-50 mt-1 w-56 max-h-60 overflow-y-auto bg-white border border-slate-200 rounded-sm shadow-lg">
                  <button onClick={() => { setSelectedFeeder(""); setShowFeederDropdown(false); setPage(0); }}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${!selectedFeeder ? "bg-slate-100 font-medium" : ""}`}>All Feeders</button>
                  {(circleFeeders.length > 0 ? circleFeeders : filterOptions.feeders || []).map(f => (
                    <button key={f.feeder_code} onClick={() => { setSelectedFeeder(f.feeder_code); setShowFeederDropdown(false); setPage(0); }}
                      className={`w-full px-3 py-2 text-left text-xs hover:bg-slate-50 ${selectedFeeder === f.feeder_code ? "bg-purple-50 text-purple-700 font-medium" : ""}`}>
                      {f.feeder_code} {f.feeder_name && `- ${f.feeder_name}`}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="w-px h-6 bg-slate-200"></div>
          <div className="flex flex-wrap gap-1">
            {["pending", "ci_done", "dtm_done", "dtc_done", "mi_done", "qc_passed"].map(status => (
              <button key={status} onClick={() => toggleStatus(status)}
                className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase border transition-colors ${selectedStatuses.includes(status) ? STATUS_COLORS[status] : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}
                data-testid={`status-pill-${status}`}>{STATUS_LABELS[status]}</button>
            ))}
          </div>
          <button onClick={() => { setSelectedHold(selectedHold === "on_hold" ? "" : "on_hold"); setPage(0); }}
            className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase border transition-colors ${selectedHold === "on_hold" ? "bg-red-100 text-red-700 border-red-300" : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"}`}
            data-testid="hold-filter-btn">Hold</button>
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700"><X size={12} />Clear</button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left" data-testid="dt-enriched-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {activeColumns.map(col => (
                  <th key={col.key} className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-slate-100 select-none"
                    onClick={() => handleSort(col.key)} data-testid={`th-${col.key}`}>
                    <div className="flex items-center gap-1">
                      {col.label}
                      {sortField === col.key ? (sortDir === "asc" ? <ArrowUp size={10} className="text-orange-500" /> : <ArrowDown size={10} className="text-orange-500" />) : <ArrowUpDown size={10} className="text-slate-300" />}
                    </div>
                  </th>
                ))}
                <th className="px-2 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">Action</th>
              </tr>
              {/* Column filter row */}
              <tr className="bg-white border-b border-slate-100">
                {activeColumns.map(col => (
                  <th key={`f-${col.key}`} className="px-1 py-1">
                    <input type="text" placeholder="..." className="w-full px-1.5 py-1 text-[10px] border border-slate-150 rounded bg-slate-50 focus:bg-white focus:border-orange-300 focus:outline-none"
                      value={columnFilters[col.key] || ""}
                      onChange={e => setColumnFilters(prev => ({ ...prev, [col.key]: e.target.value }))}
                      data-testid={`filter-${col.key}`} />
                  </th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>{[...Array(activeColumns.length + 1)].map((_, j) => (
                    <td key={j} className="px-2 py-2"><div className="h-3.5 bg-slate-100 rounded animate-pulse"></div></td>
                  ))}</tr>
                ))
              ) : filteredDTs.length === 0 ? (
                <tr><td colSpan={activeColumns.length + 1} className="text-center py-12 text-slate-400 text-sm">No DTs found.</td></tr>
              ) : (
                filteredDTs.map(dt => {
                  const nextAction = getNextAction(dt.lifecycle_status, dt.hold_status);
                  return (
                    <tr key={dt.id} className="hover:bg-slate-50/80 cursor-pointer transition-colors" onClick={() => navigate(`/dt-master/${dt.id}`)}>
                      {activeColumns.map(col => (
                        <td key={col.key} className="px-2 py-1.5">{renderCell(dt, col)}</td>
                      ))}
                      <td className="px-2 py-1.5" onClick={e => e.stopPropagation()}>
                        {nextAction ? (
                          <button onClick={() => navigate(`/${nextAction.path}/${dt.id}`)} className={`text-xs font-bold ${nextAction.color} hover:underline whitespace-nowrap`} data-testid={`dt-action-${dt.id}`}>
                            {nextAction.label} →
                          </button>
                        ) : (
                          <button onClick={() => navigate(`/dt-master/${dt.id}`)} className="text-xs text-slate-400 hover:text-slate-600">View</button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Summary Bar */}
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center gap-4 text-xs overflow-x-auto">
          <span className="text-slate-600"><span className="font-semibold">{stats.total?.toLocaleString() || 0}</span> Total</span>
          <span className="text-green-600"><span className="font-semibold">{stats.mi_done?.toLocaleString() || 0}</span> MI Done</span>
          <span className="text-blue-600"><span className="font-semibold">{inProgressCount.toLocaleString()}</span> In Progress</span>
          <span className="text-red-600"><span className="font-semibold">{holdCount.toLocaleString()}</span> Hold</span>
          <span className="text-slate-500"><span className="font-semibold">{stats.pending?.toLocaleString() || 0}</span> Pending</span>
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
            <span>Showing {page * LIMIT + 1}–{Math.min((page + 1) * LIMIT, total)} of {total}</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1 border rounded-sm disabled:opacity-40 hover:bg-slate-50">Prev</button>
              <button disabled={(page + 1) * LIMIT >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1 border rounded-sm disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl">
            <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: "Manrope" }}>Add New DT</h3>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><label className="field-label">DT Code *</label><input className="field-input" required value={newDT.dt_code} onChange={e => setNewDT({ ...newDT, dt_code: e.target.value })} data-testid="new-dt-code" /></div>
              <div><label className="field-label">DT Name</label><input className="field-input" value={newDT.dt_name} onChange={e => setNewDT({ ...newDT, dt_name: e.target.value })} /></div>
              <div><label className="field-label">Feeder *</label>
                <select className="field-select" required value={newDT.feeder_code} onChange={e => setNewDT({ ...newDT, feeder_code: e.target.value })}>
                  <option value="">Select Feeder</option>
                  {feeders.map(f => <option key={f.feeder_code} value={f.feeder_code}>{f.feeder_code} - {f.feeder_name}</option>)}
                </select>
              </div>
              <div><label className="field-label">Address</label><input className="field-input" value={newDT.address} onChange={e => setNewDT({ ...newDT, address: e.target.value })} /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600 disabled:opacity-60">{creating ? "Creating..." : "Create DT"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
